import { FC, useCallback } from 'react';
import { makeStyles } from 'tss-react/mui';
import { Button, Theme, Typography, useTheme } from '@mui/material';
import { AppPages, navigate, navigateToIssues } from '../routing/app-navigator';
import { severities } from '../../executive-summary/utils/severity-utils';
import { SeverityAlert } from 'common-types';

const SeverityBox: FC<React.PropsWithChildren<SeverityBoxProps>> = ({
  severityBreakDown,
  totalIssues,
  className,
}) => {
  const { classes, cx } = useStyles();
  const theme = useTheme();

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLSpanElement>) => {
      const { name } = event.currentTarget.dataset;
      name && navigateToIssues({ criticality: [name] });
    },
    [],
  );

  return (
    <div className={cx(classes.severityBox, className)}>
      <div className={classes.severities}>
        {Object.keys(severityBreakDown).map(
          severity =>
            severityBreakDown[severity] > 0 && (
              <Button
                key={severity}
                sx={{ textTransform: 'none', padding: 0 }}
                color='inherit'
                data-name={severities.find(s => s.key === severity)?.title}
                onClick={handleClick}>
                <Typography component='div' variant='body1' px={1}>
                  <span
                    style={{
                      backgroundColor:
                        theme.palette.categoryRiskColor[severity],
                    }}
                    className={classes.legendIcon}
                  />
                  <span style={{ fontWeight: 'bold' }}>
                    {severityBreakDown[severity].toLocaleString() + ' '}
                  </span>
                  {severities.find(s => s.key === severity)?.title}
                </Typography>
              </Button>
            ),
        )}
      </div>
      {totalIssues && (
        <Button
          onClick={() => navigate(AppPages.Issues)}
          color='inherit'
          sx={{ textTransform: 'none', marginBlockStart: 0.5 }}>
          <Typography
            variant='body2'
            fontWeight='bold'
            className={classes.totalIssues}>
            Total Issues: {totalIssues?.toLocaleString()}
          </Typography>
        </Button>
      )}
    </div>
  );
};

const useStyles = makeStyles()((theme: Theme) => ({
  severityBox: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  severities: {
    display: 'inherit',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    alignItems: 'flex-start',
  },
  totalIssues: {
    color:
      theme.palette.mode === 'light'
        ? theme.palette.grey[600]
        : theme.palette.text.primary,
  },
  legendIcon: {
    marginInlineEnd: theme.spacing(1.5),
    height: 8,
    width: 8,
    borderRadius: '50%',
    display: 'inline-block',
  },
}));

export interface SeverityBoxProps {
  severityBreakDown: SeverityAlert;
  totalIssues?: number;
  className?: string;
}

export default SeverityBox;
